import { useQueryClient } from '@tanstack/react-query';
import useInterval from 'beautiful-react-hooks/useInterval';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { v4 as uuidV4 } from 'uuid';
import config from '../../../../config';
import { useCreateSessionMutation } from '../../../../generated/core-api/graphql-react-query';
import sendNotification from '../../../../notifications/sendNotification';
import useProjectIdContext from '../../../Contexts/ProjectContexts/ProjectIdContext/useProjectIdContext';
import { useUserInfoData } from '../../../Hooks/useUserInfo';
import useIsWindowVisible from '../../Hooks/useIsWindowVisible';
import useMultiLanguageContext from '../../MultiLanguageContext/useMultiLanguageContext';
import useSessionInfoContext from '../SessionInfoContextProvider/useSessionInfoContext';
import { SessionManagerContext } from './useSessionManagerContext';

interface ReloadWebAppMessage {
  message: 'reload-webapp';
  force: boolean;
}
export type SessionWebhookResponse = (
  ReloadWebAppMessage
);

export default function SessionManagerContextProvider(props: { children: React.ReactNode }) {
  const { children } = props;
  const notificationSettings = useUserInfoData().appSettings?.notifications;
  const { isVisible } = useIsWindowVisible();
  const { t } = useTranslation('notifications');
  const {
    setSessionId, session, setEndCause, sessionId,
  } = useSessionInfoContext();
  const { projectId, setProjectId } = useProjectIdContext();
  const sessionIdRef = React.useRef(sessionId);
  const { language } = useMultiLanguageContext();
  const languageRef = React.useRef(language);
  React.useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  React.useEffect(() => {
    languageRef.current = language;
  }, [language]);

  const {
    sendMessage, lastMessage, readyState, lastJsonMessage,
  } = useWebSocket<SessionWebhookResponse>(config.Live.WebSocketUrl, {
    reconnectAttempts: Infinity,
    shouldReconnect: () => true,
    reconnectInterval(lastAttemptNumber) {
      if (lastAttemptNumber < 10) {
        return 500;
      }
      return Math.min(2 ** (lastAttemptNumber - 10) * 1000, 60000);
    },
    onError: () => { console.error('Web socket connection error'); },
  });

  React.useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      sendMessage(JSON.stringify({ cookies: document.cookie, type: 'auth' }));
    }
  }, [readyState]);

  useInterval(() => {
    if (readyState === ReadyState.OPEN) {
      sendMessage(JSON.stringify({ type: 'ping' }));
    }
  }, 60000);

  const queryClient = useQueryClient();

  const startMutation = useCreateSessionMutation({
    onSuccess(data) {
      setSessionId(data.createSession.id);
      queryClient.setQueryData(
        ['getLiveSession', { input: { id: data.createSession.id } }],
        { getSession: { ...data.createSession, detections: [] } },
      );
    },
    onError(error) {
      console.error('Error creating new session', error);
    },
  });

  const startSession = React.useCallback((args: { projectId?: string, externalCallId?: string, id?: string }) => {
    const { projectId: sessionProjectId, externalCallId, id } = args;
    if (sessionProjectId && sessionProjectId !== projectId) { setProjectId(sessionProjectId); }
    if (projectId) {
      startMutation.mutate({ input: { projectId: sessionProjectId ?? projectId, externalCallId, id } });
    }
  }, [startMutation]);

  const sessionRef = React.useRef(session);
  const projectIdRef = React.useRef(projectId);

  React.useEffect(() => {
    projectIdRef.current = projectId;
  }, [projectId]);

  React.useEffect(() => {
    if (sessionRef.current && projectId && projectIdRef.current !== projectId) {
      const externalCallId = sessionRef.current?.externalCallId;
      setEndCause('ProjectChanged');
      startSession({ projectId, externalCallId });
    } else if (sessionRef.current && language && languageRef.current !== language) {
      setEndCause('LanguageChanged');
      startSession({ projectId, externalCallId: sessionRef.current?.externalCallId });
    }
  }, [projectId, projectIdRef, sessionRef, language, languageRef]);

  React.useEffect(() => {
    if (lastMessage) {
      const data = JSON.parse(lastMessage.data);
      if (data.message) {
        switch (data.message) {
          case 'startSession':
            if (!isVisible && notificationSettings?.notifyOnExternalCall) {
              sendNotification(t('notification.new-session.title'), { body: t('notification.new-session.body') });
            }
            startSession({
              id: uuidV4(),
              projectId: data.session?.projectId,
              externalCallId: data.session?.cogneedId,
            });
            break;

          case 'stopSession':
            setEndCause('ExternalCallEnd');
            break;

          case 'OK':
            sendMessage(JSON.stringify({ type: 'ActionTimestamp', message: 'WebNavigation' }));
            break;

          default:
            break;
        }
      }
    }
  }, [lastMessage]);

  React.useEffect(() => {
    if (lastJsonMessage) {
      switch (lastJsonMessage.message) {
        case 'reload-webapp':
          if (!sessionIdRef.current || lastJsonMessage.force) {
            window.location.reload();
          }
          break;

        default:
          break;
      }
    }
  }, [lastJsonMessage]);

  const value = React.useMemo(() => ({
    startSession() {
      startSession({ projectId });
    },
    sessionLoading: startMutation.isLoading,
  }), [startSession]);

  return (
    <SessionManagerContext.Provider value={value}>
      {children}
    </SessionManagerContext.Provider>
  );
}
